import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as queryString from 'query-string';
import { Container } from 'typescript-ioc/es5';
import { NotificationService } from '../../../services';
import { AppState } from '../../../stores/app';
import { PageHeader } from '../../../components/page-header/page-header';
import { tryParseInt } from '../../../_utils/try-parse';
import { useRefEffect } from '../../../hooks/use-ref-effect';
import { getCustomMacroFields, getDomainKeywords } from '../../../_utils/domain';
import NotificationsLoader from '../../../components/notification-builder/notifications-loader';
import NotificationBuilder from '../../../components/notification-builder/notification-builder';
import NotificationBuilderAside from '../../../components/notification-builder/elements/notification-builder-aside';
import NotificationBuilderReach from '../../../components/notification-builder/elements/notification-builder-reach';
import NotificationBuilderPreview from '../../../components/notification-builder/elements/notification-builder-preview';
import NotificationBuilderMain from '../../../components/notification-builder/elements/notification-builder-main';
import NotificationTestBuilder from '../../../components/notification-builder/elements/notification-test-builder';
import NotificationAudienceBuilder
    from '../../../components/notification-builder/elements/notification-audience-builder';
import NotificationVariantBuilder from '../../../components/notification-builder/elements/notification-variant-builder';
import { useActiveDomainChangeEffect } from '../../../hooks/use-active-domain-change-effect';
import {
    buildPreviewOptions,
    buildSubmittedNotificationPackages
} from '../../../components/notification-builder/helpers';
import {
    NotificationChannelSelector
} from '../../../components/notification-builder/elements/notification-channel-selector';
import {
    FEAT_CHANNEL_ANDROID,
    FEAT_CHANNEL_IOS,
} from '../../../constants';
import { ApiVersion } from '../../../enums/api-version.enum';

export const EditNotification2 = () => {
    useActiveDomainChangeEffect((activeDomain, appSvc) => {
        appSvc.routeWithinDomain('/notifications');
    });

    const { push: updateRoute, replace: replaceRoute } = useHistory();
    const qs = queryString.parse(location.search);
    const appState = Container.get(AppState);
    const notifService: NotificationService = Container.get(NotificationService);

    const domain = appState.currentDomain!;
    const { notificationId } = useParams();
    const requestedNotifId = tryParseInt(notificationId!) || undefined;
    const requestedTestId = tryParseInt(qs.test?.toString() ?? '') || undefined;

    const [keywordOptions] = useRefEffect(() => getDomainKeywords(domain.id), [domain.id]);
    const [customMacroOptions] = useRefEffect(() => getCustomMacroFields(domain.id), [domain.id]);

    const goBackToNotifs = () => {
        updateRoute(`/domains/${domain.id}/notifications`);
    };

    const nativeIOSFlag = appState.flags.findActive(FEAT_CHANNEL_IOS)?.getKey();
    const domainHasNativeIOS = domain.flags.includes(nativeIOSFlag!);
    const nativeAndroidFlag = appState.flags.findActive(FEAT_CHANNEL_ANDROID)?.getKey();
    const domainHasNativeAndroid = domain.flags.includes(nativeAndroidFlag!);
    const domainHasNativePush = (domainHasNativeIOS && domain.nativeApnsConfiguration?.isActive) || (domainHasNativeAndroid && domain.nativeFcmConfiguration?.isActive);

    return (
        <>
            <PageHeader
                title="Edit Notification"
                append={`#${requestedNotifId}`}
            />

            <NotificationsLoader
                domainId={domain.id}
                notifId={requestedNotifId!}
                testId={requestedTestId}
            >
                {(notifications) => {
                    return (
                        <NotificationBuilder
                            mode="edit"
                            domain={domain}
                            notifications={notifications}
                            defaultSelectedId={requestedNotifId}
                        >
                            {props => {
                                const {
                                    builder, dispatchChanges, validateSubmit, onTestChange,
                                    onChannelChange, onAudienceChange, onDistributionChange,
                                    variants, onVariantChange, variantManager,
                                } = props;
                                const activeVariant = variants[builder.selectedVariantIdx];
                                const deliveryHasStarted = variants.some(v => v.getDisplayMeta().delivering);
                                const activeVariantNotEditable = activeVariant.getDisplayMeta().notEditable ?? false;
                                const disabled = deliveryHasStarted || activeVariantNotEditable;
                                const previewOptions = buildPreviewOptions(builder.channels);


                                return (
                                    <>
                                        <NotificationBuilderAside>
                                            <NotificationBuilderReach
                                                domain={domain}
                                                builder={builder}
                                                setBuilder={dispatchChanges}
                                                hideVariantReach={true}
                                                onDistributionChange={onDistributionChange}
                                            />
                                            <NotificationBuilderPreview
                                                loading={builder.loading}
                                                domain={domain}
                                                source={activeVariant}
                                                options={previewOptions}
                                                onPreviewRequest={async type => {
                                                    const valid = await validateSubmit(builder, domain, appState.flags, true);

                                                    if (valid) {
                                                        const builds = buildSubmittedNotificationPackages(domain, builder, appState.flags, type);
                                                        const previewBuild = builds[builder.selectedVariantIdx];

                                                        const sent = notifService.sendNotification(
                                                            domain.id, previewBuild, {
                                                                cancellationKey: `notif-send-preview`,
                                                                version: ApiVersion.V4,
                                                        });
                                                    }
                                                }}
                                            />
                                        </NotificationBuilderAside>

                                        <NotificationBuilderMain>
                                            {domainHasNativePush &&
                                                <NotificationChannelSelector
                                                    loading={builder.loading}
                                                    value={builder.channels}
                                                    onChange={onChannelChange}
                                                    domain={domain}
                                                />
                                            }
                                            <NotificationTestBuilder
                                                loading={builder.loading}
                                                disabled={disabled}
                                                value={builder.test}
                                                onChange={onTestChange}
                                                toggleDisabled={requestedNotifId || requestedTestId}
                                                toggleDisabledReason={requestedTestId ?
                                                    'Existing Tests cannot be converted to single notifications.' :
                                                    'Existing Notifications cannot be converted to tests.'}
                                            />

                                            <NotificationAudienceBuilder
                                                loading={builder.loading}
                                                domain={domain}
                                                setBuilder={dispatchChanges}
                                                disabled={disabled}
                                                value={activeVariant.getAudience()}
                                                onChange={onAudienceChange}
                                            />

                                            <NotificationVariantBuilder
                                                key={activeVariant.getId()}
                                                id={builder.selectedVariantIdx}
                                                domain={domain}
                                                builder={builder}
                                                variant={activeVariant}
                                                manager={variantManager}
                                                hideImageMacroToggle={true}
                                                onChange={onVariantChange}
                                                onCancel={goBackToNotifs}
                                                onSubmit={async () => {
                                                    const valid = await validateSubmit(builder, domain, appState.flags);

                                                    if (valid) {
                                                        const builds = buildSubmittedNotificationPackages(domain, builder, appState.flags);

                                                        let sent;
                                                        if (builds.length === 1) {
                                                            sent = await notifService.updateNotification(
                                                                domain.id, requestedNotifId, builds[0], {
                                                                    cancellationKey: `notif-send`,
                                                                    version: ApiVersion.V4,
                                                        });

                                                            if (sent && sent.toString() !== requestedNotifId.toString()) {
                                                                // updateNotification response is notification id
                                                                replaceRoute(`/domains/${domain.id}/notifications/edit/${sent}`);
                                                            }
                                                        } else {
                                                            sent = await notifService.updateTest(
                                                                domain.id, requestedTestId, builds[0].abTest, builds, `notif-send`);
                                                        }

                                                        if (sent) {
                                                            goBackToNotifs();
                                                        }
                                                    }
                                                }}
                                                keywordOptions={keywordOptions.current}
                                                customMacroOptions={customMacroOptions.current}
                                            />
                                        </NotificationBuilderMain>
                                    </>
                                );
                            }}
                        </NotificationBuilder>
                    );
                }}
            </NotificationsLoader>
        </>
    );
}
